<template>
  <v-dialog v-model="copyDialog" width="750">
    <v-card>
      <v-card-title class="text-h5 white--text third">
        <span class="d-flex justify-left flex-grow-1">Copie de session</span>
        <v-icon size="28" color="white" class="justify-end" @click="closeModal">mdi-close</v-icon>
      </v-card-title>

      <v-card-text class="mt-2">
        <div v-if="showRadioOptions">
          <v-row no-gutters>
            <v-radio-group
              v-model="selectedOption"
              row
            >
              <v-radio
                v-for="option in radioOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
                color="third"
              />
            </v-radio-group>
          </v-row>
          <v-row v-if="!loading" no-gutters>
            <div
              v-for="(session, index) in sessionsToShow"
              :key="session.id"
              class="col col-12"
            >
              <CopyFormContent :index="index" />
              <v-divider color="red" />
            </div>
          </v-row>
          <div v-else class="d-flex justify-center align-center">
            <Loader />
          </div>
        </div>
        <div v-else>
          <v-row no-gutters>
            <CopyFormContent :index="0" />
          </v-row>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="third"
          outlined
          large
          rounded
          @click="closeModal"
        >
          <v-icon size="24" right class="mx-0 px-4">mdi-arrow-u-left-top</v-icon>
          Annuler
        </v-btn>
        <v-btn
          color="third"
          outlined
          large
          rounded
          :disabled="isButtonDisable"
          :loading="loading"
          @click="copySession"
        >
          <v-icon size="24" right class="mx-0 px-4">mdi-plus-circle-outline</v-icon>
          Confirme
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { inject, ref, computed, watch, onBeforeMount, provide } from '@vue/composition-api';

import { fromUnixTime, format, getUnixTime, add, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import Loader from '@/components/common/Loader.vue';
import CopyFormContent from './CopyFormContent.vue';

import { mandatoryRule, sortArray, sendNotification } from '@/helpers/utils';
import { startTimeOptions, endTimeOptions, generateUuid } from '@/helpers/dataManager';
import { getAssociateRecurringSessions, addHorsesToSession } from '@/helpers/amplify';
import { computeSessionUuid, computePeriodSession, sessionHaveError, isEndTimeAllowedToCreate, handleSessionsCreation, canCopyHorses } from '@/helpers/sessions';

export default {
  components: { Loader, CopyFormContent },
  setup (_, context) {
    const copyDialog = inject('copyDialog');
    const newSession = inject('newSession');
    const selectedOption = ref('SINGLE');

    const startTime = ref(null);
    const endTime = ref(null);
    const startDate = ref(null);
    const loading = ref(false);
    const selectedCheckboxs = ref({
      copyParticipants: false,
      copyInstructors: false,
      copyHorses: false
    });
    provide('selectedCheckboxs', selectedCheckboxs);

    const sessionsToCreate = ref([]);

    provide('sessionsToCreate', sessionsToCreate);

    const radioOptions = ref([
      { label: 'Cette Session', value: 'SINGLE' },
      { label: 'Toutes les sessions', value: 'ALL' }
    ]);

    const computedStartTime = computed(() => startTimeOptions());
    const computedEndTime = computed(() => endTimeOptions());
    const isSessionRecurring = computed(() => newSession.value.recurring);

    const showRadioOptions = computed(() => isSessionRecurring.value && newSession.value.replica && Object.keys(newSession.value.replica).length);

    const isButtonDisable = computed(() => sessionsToCreate.value.some(s => {
      const sessionPeriodHaveError = (s.periods || []).some(p => !isEndTimeAllowedToCreate({ start_time: p.start, end_time: p.end }, false));
      const sessionHavePeriods = !!(s.periods || []).length;

      return !s.startTimeHelper || !s.endTimeHelper || !s.date || sessionHaveError(s) || (sessionHavePeriods && sessionPeriodHaveError);
    }));

    const closeModal = () => {
      copyDialog.value = false;
    };

    const sessionsToShow = computed(() => sessionsToCreate.value.filter(s => (selectedOption.value !== 'ALL' ? newSession.value.uuid === s.uuid : true)));

    const copySessionHorses = computed(() => canCopyHorses.includes(newSession.value.type) && selectedCheckboxs.value.copyHorses);

    const copySession = async () => {
      loading.value = true;
      const sessionsToCopy = sessionsToShow.value;

      const recurring_uuid = generateUuid();

      let sessionsToHandle = [];
      const participantHorsesToAdd = [];
      const instructorHorsesToAdd = [];


      sessionsToCopy.map(session => {
        const { startTimeHelper, endTimeHelper, date, id, uuid, pre_reserved_participants, ...sessionBody } = session;

        if (!sessionBody.recurring_uuid) {
          delete sessionBody.recurring_uuid;
        }

        // sessionBody.recurring_uuid = null;
        sessionBody.recurring = false;
        sessionBody.replica = null;
        sessionBody.start_recurring_date = null;
        sessionBody.end_recurring_date = null;
        const dateFormated = format(fromUnixTime(date), 'yyyy-MM-dd');
        sessionBody.start_time = getUnixTime(new Date(`${dateFormated}T${startTimeHelper}`));
        sessionBody.end_time = getUnixTime(new Date(`${dateFormated}T${endTimeHelper}`));
        sessionBody.uuid = computeSessionUuid(sessionBody);

        if (sessionsToCopy.length > 1) {
          sessionBody.recurring_uuid = recurring_uuid;
          sessionBody.recurring = session.recurring;
          sessionBody.replica = session.replica;
        }

        // if selected time is 00:00 add one day
        if (endTimeHelper === '00:00') {
          sessionBody.end_time = getUnixTime(add(new Date(fromUnixTime(sessionBody.end_time)), { days: 1 }));
        }

        if (copySessionHorses.value && sessionBody.horses?.length) {
          sessionBody.horses.forEach(h => {
            if (h.isInstructorHorse) {
              const instructorIndex = (sessionBody.instructors || []).findIndex(i => i.horse === h.uuid);
              if(instructorIndex !== -1 && selectedCheckboxs.value.copyInstructors) {
                instructorHorsesToAdd.push({ ...h, instructor: sessionBody.instructors[instructorIndex].uuid });
                return;
              }
              instructorHorsesToAdd.push(h);
              return;
            } 
            const participantIndex = (sessionBody.participants || []).findIndex(p => p.horse === h.uuid);
            if(participantIndex !== -1 && selectedCheckboxs.value.copyParticipants) {
              participantHorsesToAdd.push({ ...h, participant: sessionBody.participants[participantIndex].uuid });
              return;
            }
            participantHorsesToAdd.push(h);
          });
        }

        sessionBody.horses = [];
        sessionBody.participants = selectedCheckboxs.value.copyParticipants ? sessionBody.participants.map(p => ({ ...p, horse: null })) : [];
        sessionBody.instructors = selectedCheckboxs.value.copyInstructors ? sessionBody.instructors.map(i => ({ ...i, horse: null })) : [];
        if ((sessionBody.periods || []).length) {
          const sessionPeriods = computePeriodSession(sessionBody);

          sessionPeriods.map(s => sessionsToHandle.push({
            ...s,
            id: generateUuid(),
            uuid: computeSessionUuid(s),
            recurring_uuid: recurring_uuid,
            recurring: true,
            start_recurring_date: getUnixTime(startOfDay(new Date(fromUnixTime(s.start_time)))),
            end_recurring_date: getUnixTime(endOfDay(new Date(fromUnixTime(s.start_time)))),
            metadata: { copiedSession: true, copiedFrom: session.id }
          }));
          return;
        }

        sessionsToHandle.push({ ...sessionBody, id: generateUuid(), metadata: { copiedSession: true, copiedFrom: session.id } });
      });

      if (sessionsToCopy.length > 1) {
        const sessionOrderedByStartTime = sortArray(sessionsToHandle, 'start_time');

        sessionsToHandle = sessionsToHandle.map(s => ({
          ...s,
          start_recurring_date: getUnixTime(startOfDay(new Date(fromUnixTime(sessionOrderedByStartTime[0].start_time)))),
          end_recurring_date: getUnixTime(endOfDay(new Date(fromUnixTime(sessionOrderedByStartTime[sessionOrderedByStartTime.length - 1].start_time))))
        }));
      }

      if (sessionsToHandle.length === 1) {
        sessionsToHandle[0].recurring = false;
        delete sessionsToHandle[0].recurring_uuid;
      }

      const response = await handleSessionsCreation(sessionsToHandle);

      if (!response.success) {
        sendNotification('error', response.message);
        loading.value = false;
        return;
      }

      if (copySessionHorses.value && sessionsToHandle.length > 0) {
        const firstSessionId = sessionsToHandle[0].id;
        const updateType = sessionsToHandle.length > 1 ? 'ALL' : null;

        try {
          if (participantHorsesToAdd.length > 0) {
            await addHorsesToSession(
              firstSessionId,
              participantHorsesToAdd,
              updateType,
              null,
              false,
              false,
              null,
              true
            );
          }

          if (instructorHorsesToAdd.length > 0) {
            await addHorsesToSession(
              firstSessionId,
              instructorHorsesToAdd,
              updateType,
              null,
              true,
              false, 
              null,
              true
            );
          }
        } catch (error) {
          console.error('Error adding horses:', error);
          sendNotification('warning', 'Sessions créées, mais certains chevaux n\'ont pas pu être ajoutés en raison de conflits');
        }
      }

      sendNotification('success', 'Session(s) copiée(s) avec succès');
      loading.value = false;

      closeModal();
      context.emit('closeDialog');
    };

    const populateSessions = async () => {
      sessionsToCreate.value = [
        {
          ...newSession.value,
          startTimeHelper: format(new Date(fromUnixTime(newSession.value.start_time)), 'HH:mm'),
          endTimeHelper: format(new Date(fromUnixTime(newSession.value.end_time)), 'HH:mm'),
          date: getUnixTime(new Date(fromUnixTime(newSession.value.start_time))),
          place: {}
        }
      ];
      if (isSessionRecurring.value) {
        loading.value = true;

        if (selectedOption.value === 'ALL') {
          const relatedSessions = await getAssociateRecurringSessions(newSession.value);
          sessionsToCreate.value = [];

          relatedSessions.map(session => {
            const isSessionOnBag = sessionsToCreate.value.some(sess => isWithinInterval(fromUnixTime(sess.start_time), { start: startOfDay(fromUnixTime(session.start_time)), end: endOfDay(fromUnixTime(session.end_time)) }));

            if (!isSessionOnBag) {
              sessionsToCreate.value.push(session);
            }
          });
        }

        sessionsToCreate.value = sessionsToCreate.value.map(session => ({
          ...session,
          startTimeHelper: format(new Date(fromUnixTime(session.start_time)), 'HH:mm'),
          endTimeHelper: format(new Date(fromUnixTime(session.end_time)), 'HH:mm'),
          date: getUnixTime(new Date(fromUnixTime(session.start_time))),
          place: {}
        }));

        sessionsToCreate.value = sortArray(sessionsToCreate.value, 'date');
      }
      loading.value = false;
    };

    onBeforeMount(() => {
      populateSessions();
    });

    watch(isSessionRecurring, async () => {
      populateSessions();
    });

    watch(selectedOption, () => {
      populateSessions();
    });

    return { sessionsToShow, loading, sessionsToCreate, startDate, copyDialog, closeModal, newSession, selectedOption, radioOptions, startTime, endTime, mandatoryRule, computedStartTime, computedEndTime, isButtonDisable, copySession, showRadioOptions };
  }
};
</script>
